import {
    type Ref,
    type ComputedRef,
    type WritableComputedRef,
    computed,
    ref,
    watch,
    nextTick,
} from 'vue'
import { useElementBounding } from '@vueuse/core'
import type { TwoKnobbedPosition } from '@/components/ui/inputs/range/InputRangeTwoKnobbed.vue'

export function useInputRangeStyle(
    rangeClamped: ComputedRef<number>,
    rangeTotal: ComputedRef<number>,
    valueUi: WritableComputedRef<number>,
    minFinal: ComputedRef<number>,
    clampPercentLeft: ComputedRef<number>,
    clampPercentRight: ComputedRef<number>,
    props: { isPartOfTwoKnobbed: boolean; hideLabel?: boolean },
    valueFormatter: (value: number) => string,
    emit: (evt: 'two-knobbed-position', args_0: TwoKnobbedPosition) => void
): {
    thumbDiameterCss: string
    styleLabel: ComputedRef<string>
    classTwoKnobbed: ComputedRef<Record<string, boolean>>
    labelElement: Ref<HTMLLabelElement | null, HTMLLabelElement | null>
} {
    const thumbDiameter = 18
    const thumbDiameterCss = `${thumbDiameter}px`
    const labelElement: Ref<HTMLLabelElement | null> = ref(null)
    const clampedToTotal = computed(() => rangeClamped.value / rangeTotal.value)

    const progress = computed(
        () => ((valueUi.value - minFinal.value) / rangeClamped.value) * 100
    )

    const { width, left } = useElementBounding(labelElement)

    if (props.isPartOfTwoKnobbed) {
        watch(
            // we need to watch the *left* value because
            // it's 0 when a parent toggles display:none
            // this could lead to a wrong value when the slider is
            // initially rendered but has display:none
            [progress, left],
            ([progress, left]) => {
                nextTick(() => {
                    emit('two-knobbed-position', {
                        left,
                        width: width.value,
                        progress,
                        label: valueFormatter(valueUi.value),
                        styleLabel: styleLabel.value,
                        clampPercentLeft: clampPercentLeft.value,
                        clampPercentRight: clampPercentRight.value,
                        clampedToTotal: clampedToTotal.value,
                    })
                })
            },
            { immediate: true }
        )
    }

    const styleLabel = computed(() => {
        const p = progress.value

        return `left: clamp(
            -${clampPercentLeft.value / clampedToTotal.value}%,
            calc(${p}% - ${width.value / 2}px - ${
                (thumbDiameter * (p - 50)) / 100
            }px),
            calc(${
                100 + clampPercentRight.value / clampedToTotal.value
            }% - ${width.value}px)
        )`.trim()
    })

    const classTwoKnobbed = computed(() => ({
        'two-knobbed': props.isPartOfTwoKnobbed,
        'hidden': Boolean(props.hideLabel),
    }))

    return {
        thumbDiameterCss,
        styleLabel,
        classTwoKnobbed,
        labelElement,
    }
}
